import React, { useState, useEffect } from 'react';
import { Header } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import { compose } from 'redux';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { connect } from 'react-redux';

import leftKey from '@plone/volto/icons/left-key.svg';
import rightKey from '@plone/volto/icons/right-key.svg';
import clearSVG from '@plone/volto/icons/clear.svg';

import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';

import DatePicker from 'react-datepicker';
import './DateRangeFacet.css';

const messages = defineMessages({
  year: { id: 'Year', defaultMessage: 'Year' },
});

const PrevIcon = () => <Icon name={leftKey} size="20px" />;
const NextIcon = () => <Icon name={rightKey} size="20px" />;
const CloseIcon = () => <Icon name={clearSVG} size="16px" />;

const YearFacet = (props) => {
  const { facet, onChange, value, intl, moment } = props;

  const currentYear = moment.default().year();

  // FIXED: Use null as initial state when no value exists
  // This way we can distinguish between "no selection" and "current year selected"
  const yearFromValue =
    value && value[0] ? parseInt(value[0], 10) : null;

  const [selectedYear, setSelectedYear] = useState(yearFromValue);

  // keep local UI in sync with facet state
  useEffect(() => {
    const yr = value && value[0] ? parseInt(value[0], 10) : null;
    setSelectedYear(yr);
  }, [value]);

  const handleYearChange = (date) => {
    const year = date.getFullYear();
    setSelectedYear(year);

    // FIXED: Always call onChange even if it's the current year
    // This ensures the facet is properly applied
    onChange(facet.field.value, [String(year)]);
  };

  const handleClear = () => {
    setSelectedYear(null);
    // clear facet completely
    onChange(facet.field.value, []);
  };

  return (
    <div className="daterange-facet">
      <Header as="h4">{facet?.title ?? facet?.field?.label}</Header>

      <div className="ui form date-time-widget-wrapper">
        <div className="ui input date-input" style={{ position: 'relative' }}>
          <DatePicker
            selected={selectedYear ? new Date(selectedYear, 0, 1) : null}
            onChange={handleYearChange}
            showYearPicker
            dateFormat="yyyy"
            className="year-picker-input"
            calendarClassName="year-picker-calendar"
            placeholderText={intl.formatMessage(messages.year)}
            renderCustomHeader={({
              date,
              decreaseYear,
              increaseYear,
              prevYearButtonDisabled,
              nextYearButtonDisabled,
            }) => (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0 10px',
                }}
              >
                <button
                  type="button"
                  onClick={decreaseYear}
                  disabled={prevYearButtonDisabled}
                  className="nav-btn prev-btn"
                >
                  <PrevIcon />
                </button>
                <span>{date.getFullYear()}</span>
                <button
                  type="button"
                  onClick={increaseYear}
                  disabled={nextYearButtonDisabled}
                  className="nav-btn next-btn"
                >
                  <NextIcon />
                </button>
              </div>
            )}
          />

          {value && value[0] && (
            <button
              type="button"
              className="clear-btn"
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
              }}
              onClick={handleClear}
            >
              <CloseIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Map internal search-block state -> facet value
 * We keep the convention that value is ["YYYY"] or [].
 */
YearFacet.stateToValue = ({ selectedValue }) => {
  if (!selectedValue || selectedValue.length === 0) {
    return [];
  }
  if (typeof selectedValue[0] === 'string') {
    return [selectedValue[0]];
  }
  return [];
};

/**
 * Convert that ["YYYY"] value into a date.between query for the backend.
 */
YearFacet.valueToQuery = ({ value, facet }) => {
  if (!value || !value[0]) return null;

  const year = parseInt(value[0], 10);
  if (Number.isNaN(year)) return null;

  const start = `${year}-01-01`;
  const end = `${year}-12-31`;

  return {
    i: facet.field.value, // e.g. "effective"
    o: 'plone.app.querystring.operation.date.between',
    v: [start, end],
  };
};

export default compose(
  injectLazyLibs(['moment']),
  connect((state) => ({ lang: state.intl.locale })),
  injectIntl,
)(YearFacet);
